/**
 * Free-Tier Access Rules — single source of truth (client + server).
 *
 * Rules:
 *   - Kapitel 1 is fully free.
 *   - Premium users have full access to all chapters.
 *   - Referral bonus chapters grant full access (same as premium for those chapters).
 *   - Otherwise (free user, chapter 2–14): only FREE_PREVIEW_COUNT questions per
 *     chapter are accessible. Which 5 are picked is controlled by
 *     PREVIEW_QUESTION_IDS (curated); when no curation exists for a chapter,
 *     a deterministic natural-sort fallback picks the first 5 by question ID.
 */

import type { Question, UserProfile } from './types';

export const FREE_PREVIEW_COUNT = 5;
export const FREE_FULL_CHAPTER_ID = 1;

/**
 * Curated preview question IDs per chapter (chapters 2–14).
 *
 * To curate: list 5 stable question IDs per chapter that represent it well
 * (mix of difficulties, common exam topics). Any unknown ID is ignored and
 * topped up from the deterministic fallback.
 *
 * Leave empty to use deterministic fallback (natural-sort by question index).
 */
export const PREVIEW_QUESTION_IDS: Record<number, string[]> = {
  // 2: ['c2_q1', 'c2_q5', 'c2_q12', 'c2_q23', 'c2_q40'],
  // 3: ['c3_q1', ...],
};

type AccessUser = Pick<UserProfile, 'isPremium' | 'bonusChapters'>;

export function isFullyFreeChapter(chapterId: number): boolean {
  return chapterId === FREE_FULL_CHAPTER_ID;
}

/** True when the user has full access to every question of the chapter. */
export function canAccessFullChapter(user: AccessUser, chapterId: number): boolean {
  if (user.isPremium) return true;
  if (isFullyFreeChapter(chapterId)) return true;
  const bonus = user.bonusChapters || 0;
  return chapterId <= FREE_FULL_CHAPTER_ID + bonus;
}

function parseQuestionIndex(id: string): number {
  const m = id.match(/_q(\d+)/);
  return m ? parseInt(m[1], 10) : Number.MAX_SAFE_INTEGER;
}

function naturalSortIds(ids: string[]): string[] {
  return [...ids].sort((a, b) => {
    const ai = parseQuestionIndex(a);
    const bi = parseQuestionIndex(b);
    if (ai !== bi) return ai - bi;
    return a.localeCompare(b);
  });
}

/**
 * Returns the IDs of preview questions available to free users for a chapter.
 * Uses curated IDs first, fills with deterministic fallback if curation is
 * missing or incomplete. Never returns more than FREE_PREVIEW_COUNT.
 */
export function getPreviewQuestionIds(
  chapterId: number,
  allChapterQuestionIds: string[]
): string[] {
  const existing = new Set(allChapterQuestionIds);
  const curated = (PREVIEW_QUESTION_IDS[chapterId] || []).filter((id) => existing.has(id));

  if (curated.length >= FREE_PREVIEW_COUNT) {
    return curated.slice(0, FREE_PREVIEW_COUNT);
  }

  const fallback = naturalSortIds(allChapterQuestionIds);
  const result: string[] = [...curated];
  for (const id of fallback) {
    if (result.length >= FREE_PREVIEW_COUNT) break;
    if (!result.includes(id)) result.push(id);
  }
  return result;
}

/** Filters a chapter's questions to those a free user is allowed to access. */
export function getAccessibleQuestionsForChapter(
  user: AccessUser,
  chapterId: number,
  chapterQuestions: Question[]
): Question[] {
  if (canAccessFullChapter(user, chapterId)) return chapterQuestions;
  const previewIds = new Set(
    getPreviewQuestionIds(
      chapterId,
      chapterQuestions.map((q) => q.id)
    )
  );
  const filtered = chapterQuestions.filter((q) => previewIds.has(q.id));
  // Preserve curated order when curation drives the selection.
  const order = getPreviewQuestionIds(
    chapterId,
    chapterQuestions.map((q) => q.id)
  );
  return order.map((id) => filtered.find((q) => q.id === id)).filter((q): q is Question => !!q);
}

/** True when the user is allowed to access this specific question. */
export function canAccessQuestion(
  user: AccessUser,
  question: Pick<Question, 'id' | 'chapterId'>,
  allChapterQuestionIds: string[]
): boolean {
  if (canAccessFullChapter(user, question.chapterId)) return true;
  const previewIds = getPreviewQuestionIds(question.chapterId, allChapterQuestionIds);
  return previewIds.includes(question.id);
}

/**
 * Filters a heterogeneous question list (e.g. SRS-due, failed, smart-session)
 * to only those the user can access. Free users keep questions from chapter 1
 * plus preview questions of locked chapters; premium users get everything.
 */
export function filterAccessibleQuestions(
  user: AccessUser,
  questions: Question[],
  allQuestions: Question[]
): Question[] {
  if (user.isPremium && !user.bonusChapters) {
    // Pure premium short-circuit (most common premium case)
    return questions;
  }
  // Pre-compute chapter → ids map once for efficiency
  const idsByChapter = new Map<number, string[]>();
  for (const q of allQuestions) {
    const arr = idsByChapter.get(q.chapterId);
    if (arr) arr.push(q.id);
    else idsByChapter.set(q.chapterId, [q.id]);
  }
  return questions.filter((q) => canAccessQuestion(user, q, idsByChapter.get(q.chapterId) || []));
}
